import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const row1 = categories.slice(0, 5);
const row2 = categories.slice(5, 10);
const row3 = categories.slice(10, 14);

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  const textColor = scrolled ? "text-foreground-950" : "text-background-50";
  const textMuted = scrolled ? "text-foreground-600" : "text-background-200";
  const textSub = scrolled ? "text-foreground-700" : "text-background-100";
  const btnPrimary = scrolled
    ? "bg-foreground-950 text-background-50 hover:bg-foreground-800"
    : "bg-background-50 text-foreground-950 hover:bg-background-100";
  const btnAccent = scrolled
    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
    : "bg-accent-500 text-background-50 hover:bg-accent-400";
  const dropdownBtn = dropdownOpen
    ? "bg-foreground-950 text-background-50"
    : scrolled
      ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
      : "text-background-100 hover:text-background-50 hover:bg-white/10";
  const hamburgerColor = scrolled ? "text-foreground-950" : "text-background-50";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 h-[72px] flex items-center transition-all duration-300 ${
        scrolled
          ? "bg-background-50/85 backdrop-blur-md shadow-[0_1px_0_0_rgba(0,0,0,0.06)]"
          : "bg-transparent"
      }`}
    >
      <div className="w-full max-w-[1440px] mx-auto flex items-center justify-between px-4 md:px-10 h-full">
        {/* Left - Brand + Nav Links */}
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className={`text-lg md:text-xl font-bold font-heading tracking-tight ${textColor}`}>
              OOPBuy <span className={`font-normal ${textMuted}`}>Spreadsheets</span>
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  : "text-background-100 hover:text-background-50 hover:bg-white/10"
              }`}
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${dropdownBtn}`}
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-[calc(100%+8px)] left-0 bg-background-50 rounded-2xl shadow-xl border border-background-200/70 w-[640px] z-50">
                  <div className="px-6 py-6 space-y-4">
                    <div className="grid grid-cols-3 gap-2">
                      {categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          onClick={() => setDropdownOpen(false)}
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors duration-150 group"
                        >
                          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 group-hover:text-foreground-950 transition-colors shrink-0">
                            <i className={`${cat.icon} text-sm`}></i>
                          </span>
                          <span className="text-sm font-medium text-foreground-800 group-hover:text-foreground-950 transition-colors whitespace-nowrap">
                            {cat.name}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/article/what-is-oopbuy-spreadsheet"
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                  : "text-background-100 hover:text-background-50 hover:bg-white/10"
              }`}
            >
              Shopping Guide
            </Link>
          </div>
        </div>

        {/* Right - Desktop CTA Buttons */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center justify-center h-10 px-5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${btnPrimary}`}
          >
            OOPBuy Spreadsheet
          </a>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center justify-center h-10 px-5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${btnAccent}`}
          >
            Buy It Now
          </a>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${hamburgerColor}`}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Mobile Menu Panel */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-[72px] bg-background-50 z-40 overflow-y-auto">
          <div className="px-5 py-5 space-y-5">
            {/* Mobile Nav Links */}
            <div className="space-y-1">
              <Link
                to="/"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-800 font-medium"
              >
                <i className="ri-home-4-line text-lg"></i>
                Home
              </Link>

              <Link
                to="/article/what-is-oopbuy-spreadsheet"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors text-foreground-800 font-medium"
              >
                <i className="ri-book-open-line text-lg"></i>
                Shopping Guide
              </Link>
            </div>

            {/* Categories */}
            <div>
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-[0.2em] font-label px-4 mb-2">
                Categories
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMobile}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors duration-150 group"
                  >
                    <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 group-hover:text-foreground-950 transition-colors shrink-0">
                      <i className={`${cat.icon} text-sm`}></i>
                    </span>
                    <span className="text-sm text-foreground-800 group-hover:text-foreground-950 transition-colors">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="pt-3 space-y-2.5 px-4">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center h-11 w-full rounded-full bg-foreground-950 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                OOPBuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobile}
                className="flex items-center justify-center h-11 w-full rounded-full bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}